import { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { VerticaisManager, paths } from "src/components/vendas/VerticaisManager";
import api, { IDataRequest, STATUS_CODE, apiNoLoading } from "src/framework/providers/api";
import { getEstados, getEstadosServico, getRegioes } from "src/store/global.store";
import { ProjetoTipoType } from "src/components/projetos/types";
import { MENSAGEM_VIGENCIA_SUBSTITUICAO_CONTA_ENCERRADO } from "src/App";
import { useProjetosOffGridStore } from "src/store/vendas/projetos/offgrid.store";
import { ProjetoHelperOffGrid, ProjetoOutputOffGrid } from "../../projetoHelperOffGrid";
import { ComponenteProjetoComQuantidade } from "../../types";
import { ComponenteProvider } from "src/providers/estoque/ComponenteProvider";
import { notifierStore } from "src/framework/store/NotifierStore";
import { ModalInfoInativos } from "../components/ModalItensInativos";
import { verificarRestricoesProjeto } from "src/components/projetos/utils/verificarRestricoesComponentesProjeto";

const verticaisManager = new VerticaisManager(paths, api, apiNoLoading)
const componenteProvider = new ComponenteProvider()

const loadCodProjeto = async (id: string, onFail: () => void) => {
  const request: IDataRequest = {
    url: `/projetos?id=${id}`
  }
  const response = await apiNoLoading.get(request)
  if(response.statusCode !== STATUS_CODE.OK){
      const message: string[] = Array.isArray(response?.message) ? response?.message : [response?.message];
      const mensagemVigencia = message?.includes(MENSAGEM_VIGENCIA_SUBSTITUICAO_CONTA_ENCERRADO) || message?.some(m => m?.includes(MENSAGEM_VIGENCIA_SUBSTITUICAO_CONTA_ENCERRADO));
      if (mensagemVigencia) {
          notifierStore.setNotifier({
            title: "Aviso!!!",
            content: [MENSAGEM_VIGENCIA_SUBSTITUICAO_CONTA_ENCERRADO],
            timeOut: 3500,
            onFinishTimeout: () => {
              onFail();
            },
            onClose: () => {
              notifierStore.setOpen(false);
              onFail();
            }
          });
          notifierStore.setOpen(true);
      }
  }
}

export function EdicaoLayoutOffGrid({ children, clonarProjeto = false }: { children: React.ReactNode; clonarProjeto?: boolean }){
  const projetosOffGridStoreState = useProjetosOffGridStore(store => store.state.data)
  const projetosOffGridStoreActions = useProjetosOffGridStore(store => store.actions)
  const { reset, changeValue, addModulos, addSolucaoCalculada } = useProjetosOffGridStore(store => store.actions)

  const [itensInativos, setItensInativos] = useState<Pick<ComponenteProjetoComQuantidade, "nome" | "categoria_componentes_id">[]>([]);

  const { id } = useParams<{id: string}>()
  const history = useHistory();

  const [finished, setFinished] = useState(false)

  const notFoundProjectAlert = useCallback((messages?: string[])=>{
    notifierStore.setNotifier({
      title: 'Atenção',
      content: messages?.length ? messages : 'Projeto não encontrado',
      onClose: () => {
        notifierStore.setOpen(false)
        history.push('/projetos/menu-projetos')
      },
      onFinishTimeout: () => {
        history.push('/projetos/menu-projetos')
      },
      timeOut: 6000,
    })
    notifierStore.setOpen(true)
  },[history])

  const routeIsValid = useCallback((tipo: ProjetoTipoType)=>{
    return true;
  },[])
  
  const fetch = useCallback(async()=>{
    if (id && !projetosOffGridStoreState.isEdit) {
      await loadCodProjeto(id, () => {
        history.push('/projetos/menu-projetos')
      });
      const [estados, regioes, estadosServico] = await Promise.all([
        getEstados({ force: true }),
        getRegioes(),
        getEstadosServico(),
      ])

      if (!estados.length || !regioes.length) return;

      const projetoResult = await verticaisManager.find<ProjetoOutputOffGrid[]>({
        primaryKey: id,
        includes: ["componentes.componente", "componentes.componente.atributos", "equipamentos", "endereco", "cidade", "solicitacao", "revisoes", "componentes", "estruturas_grupos", "ev", "analista", "distribuidor", "revenda", "uploads", "disposicoes", "grupo_estrutura", "anexos", "descontos", "componentes_modificados"]
      })

      if (projetoResult?.statusCode !== STATUS_CODE.OK || !projetoResult?.data?.length) {
        const message: string[] = (projetoResult?.message && (Array.isArray(projetoResult?.message) ? projetoResult?.message : [projetoResult?.message])) || [];
        const mensagemVigencia = message?.includes(MENSAGEM_VIGENCIA_SUBSTITUICAO_CONTA_ENCERRADO) || message?.some(m => m?.includes(MENSAGEM_VIGENCIA_SUBSTITUICAO_CONTA_ENCERRADO));
        if (mensagemVigencia) {
          notFoundProjectAlert(message)
        } else {
          notFoundProjectAlert()
        }
        return;
      }

      const projeto = projetoResult.data[0];
      if (!projeto) return;

      if (!routeIsValid(projeto.tipo)) {
        reset()
        history.replace("/projetos", { done: true })
        return;
      }

      const componentsIds = (projeto?.componentes?.filter(comp=>comp.quantidade > 0))?.map(x=>x.componente.id);
      const resultComponentes = await componenteProvider.buscarComponentesPorId(componentsIds || []);

      const componentesDoProjetoComQuantidade: ComponenteProjetoComQuantidade [] = []
      projeto.componentes?.forEach((savedComponente) => {
        const grupos = savedComponente.estruturas_grupos || [] 
        if (grupos.length > 0) {
          grupos.forEach(item => {
            const comp = resultComponentes?.find((cachedComponente: any) => cachedComponente.id === item.componente_id)
            if (comp) componentesDoProjetoComQuantidade.push({...comp, ...item, valor_unitario: savedComponente.valor_unitario })
          })
        } else {
          const comp = resultComponentes?.find((cachedComponente: any) => cachedComponente.id === savedComponente.componente.id)
          if (comp) componentesDoProjetoComQuantidade.push({...comp, ...savedComponente })
        }
      })

      const projetoHelper = new ProjetoHelperOffGrid(projeto, { 
        estados: estados || [],
        regioes: regioes || [],
        estadosServico: estadosServico || [],
        clonarProjeto,
      })

      const output = await projetoHelper.formatar()

      output.old_componentes_edicao = componentesDoProjetoComQuantidade || []
      output.old_estado_id = projeto?.estado_id;

      if (output.fileiras_invalidas !== undefined) {
        delete output.fileiras_invalidas;
      }
      
      changeValue({
        ...output,
        estruturas: projetoHelper.estruturasListagem.ativos,
        todas_estruturas: projetoHelper.estruturasListagem.todos,
        estruturas_calculadas: projetoHelper.estruturasCalculadas,
        baterias: projetoHelper.bateriasListagem,
        acessorios: projetoHelper.acessoriosCalculados,
        valor_minimo_servico: projetoHelper.valorMinimoServico?.valor_minimo_servico || 0,
        reativar: history.location.search.includes('reativar'),
        recalcular: history.location.search.includes('reativar'),
      });

      addModulos(projetoHelper.modulosListagem, projetoHelper.moduloSelecionado ? {
        id: projetoHelper.moduloSelecionado?.componente.id,
        quantidade: projetoHelper.moduloSelecionado?.quantidade || 0,
      } : undefined);

      if (projetoHelper.solucaoCalculada) {
        addSolucaoCalculada(projetoHelper.solucaoCalculada);
      }

      const componentesComRestricoes = await verificarRestricoesProjeto(projeto.id, projeto.componentes)

      if (componentesComRestricoes.length) {
        setItensInativos(componentesComRestricoes || []);
      }
    }
  },[addModulos, clonarProjeto, addSolucaoCalculada, changeValue, history, id, notFoundProjectAlert, projetosOffGridStoreState.isEdit, reset, routeIsValid])

  useEffect(()=>{
    fetch().then(() => {
      setFinished(true)
    })
  },[fetch])

  if (!projetosOffGridStoreState.id || !finished) {
    return null;
  }

  return (
    <>
      {children}
       <ModalInfoInativos
        open={itensInativos.length > 0}
        onClose={()=>setItensInativos([])}
        itensInativos={itensInativos}
      />
    </>
  )
}

